import { Box, Button, ButtonProps, CircularProgress } from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import React from 'react';

import { MobileCaption } from '../Typography';

export type CustomButtonVariant =
  | 'primary'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'default';

const VARIANT_COLORS: Record<CustomButtonVariant, string> = {
  primary: BASE_COLORS.blue,
  success: BASE_COLORS.darkGreen,
  warning: BASE_COLORS.yellow,
  error: BASE_COLORS.red,
  info: BASE_COLORS.blue,
  default: BASE_COLORS.gray,
};

export interface CustomButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: CustomButtonVariant;
  loading?: boolean;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  variant = 'default',
  children,
  sx,
  disabled,
  loading = false,
  ...buttonProps
}) => {
  const baseColor = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;
  const isDisabled = disabled ?? loading;

  return (
    <Button
      disableElevation
      {...buttonProps}
      disabled={isDisabled}
      sx={{
        minWidth: 0,
        px: 2,
        py: 1,
        borderRadius: 5,
        boxShadow: 1,
        backgroundColor: baseColor,
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.8125rem',
        textTransform: 'none',
        '&:hover': {
          backgroundColor: baseColor,
          color: '#fff',
          opacity: 0.9,
        },
        '&:disabled': {
          backgroundColor: baseColor,
          color: '#fff',
          opacity: 0.5,
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: baseColor,
        },
        ...sx,
      }}
    >
      {loading && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: 1,
          }}
        >
          <CircularProgress size={18} color="inherit" sx={{ color: '#fff' }} />
        </Box>
      )}
      <MobileCaption
        component="span"
        sx={{
          fontWeight: 'inherit',
          fontSize: 'inherit',
          color: 'inherit',
        }}
      >
        {children}
      </MobileCaption>
    </Button>
  );
};

export default CustomButton;
